import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Users, 
  Plus, 
  Search,
  MoreHorizontal,
  Trash2,
  Edit,
  Loader2,
  Building2,
  User,
  Globe,
  FileText,
  Send,
  Clock,
  CheckCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DashboardLayout from "@/components/DashboardLayout";

import { API_URL } from "@/utils/api";

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "UK", name: "United Kingdom" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "AU", name: "Australia" },
  { code: "IN", name: "India" },
  { code: "OTHER", name: "Other" }
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function RecipientsPage({ user }) {
  const [recipients, setRecipients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("vendors");
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTaxFormDialog, setShowTaxFormDialog] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    recipient_type: "vendor",
    entity_type: "individual",
    address: "",
    city: "",
    state: "",
    country: "US",
    tax_id: "",
    phone: "",
    notes: ""
  });

  useEffect(() => {
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/recipients`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecipients(data);
      }
    } catch (err) {
      console.error("Error fetching recipients:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      recipient_type: activeTab === "customers" ? "customer" : "vendor",
      entity_type: "individual",
      address: "",
      city: "",
      state: "",
      country: "US",
      tax_id: "",
      phone: "",
      notes: ""
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/recipients`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to add recipient");
      }
      
      toast.success(`${formData.recipient_type === 'customer' ? 'Customer' : 'Vendor'} added successfully!`);
      setShowAddDialog(false);
      resetForm();
      fetchRecipients();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedRecipient) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/recipients/${selectedRecipient.recipient_id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update recipient");
      }
      
      toast.success("Recipient updated successfully!");
      setShowEditDialog(false);
      setSelectedRecipient(null);
      resetForm();
      fetchRecipients();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRecipient) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/recipients/${selectedRecipient.recipient_id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete recipient");
      }
      
      toast.success("Recipient deleted");
      setShowDeleteDialog(false);
      setSelectedRecipient(null);
      fetchRecipients();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestTaxForm = async (formType) => {
    if (!selectedRecipient) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/recipients/${selectedRecipient.recipient_id}/request-tax-form`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ form_type: formType })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to request tax form");
      }
      
      const result = await response.json();
      toast.success(result.message || "Tax form request sent!");
      setShowTaxFormDialog(false);
      setSelectedRecipient(null);
      fetchRecipients();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getRecommendedTaxForm = (recipient) => {
    if (!recipient) return null;
    const isUS = recipient.country === "US";
    const isCompany = recipient.entity_type === "company";
    
    if (isUS) {
      return { type: "w9", name: "W-9", description: "Request for Taxpayer Identification Number" };
    } else if (isCompany) {
      return { type: "w8bene", name: "W-8BEN-E", description: "Certificate of Status of Beneficial Owner for Tax Withholding (Entities)" };
    } else {
      return { type: "w8ben", name: "W-8BEN", description: "Certificate of Foreign Status of Beneficial Owner (Individuals)" };
    }
  };

  const openEditDialog = (recipient) => {
    setSelectedRecipient(recipient);
    setFormData({
      name: recipient.name || "",
      email: recipient.email || "",
      recipient_type: recipient.recipient_type || "vendor",
      entity_type: recipient.entity_type || "individual",
      address: recipient.address || "",
      city: recipient.city || "",
      state: recipient.state || "",
      country: recipient.country || "US",
      tax_id: recipient.tax_id || "",
      phone: recipient.phone || "",
      notes: recipient.notes || ""
    });
    setShowEditDialog(true);
  };

  const openAddDialog = () => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      recipient_type: activeTab === "customers" ? "customer" : "vendor"
    }));
    setShowAddDialog(true);
  };

  const customers = recipients.filter(r => r.recipient_type === "customer");
  const vendors = recipients.filter(r => r.recipient_type === "vendor");

  const filteredRecipients = (activeTab === "customers" ? customers : vendors).filter(r =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTaxFormStatusBadge = (recipient) => {
    const status = recipient.tax_form_status;
    switch (status) {
      case "received":
        return <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle className="w-3 h-3" /> Received</Badge>;
      case "requested":
        return <Badge className="bg-yellow-100 text-yellow-700 gap-1"><Clock className="w-3 h-3" /> Requested</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600">Not Requested</Badge>;
    }
  };

  const RecipientForm = ({ onSubmit, isEdit = false }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input
            placeholder="Contact or company name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            data-testid="recipient-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input
            type="email"
            placeholder="email@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            data-testid="recipient-email-input"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Type</Label>
          <Select
            value={formData.recipient_type}
            onValueChange={(value) => setFormData({ ...formData, recipient_type: value })}
          >
            <SelectTrigger data-testid="recipient-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" /> Customer
                </div>
              </SelectItem>
              <SelectItem value="vendor">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Vendor
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Entity Type</Label>
          <Select
            value={formData.entity_type}
            onValueChange={(value) => setFormData({ ...formData, entity_type: value })}
          >
            <SelectTrigger data-testid="entity-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="individual">Individual</SelectItem>
              <SelectItem value="company">Company</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Country</Label>
          <Select
            value={formData.country}
            onValueChange={(value) => setFormData({ ...formData, country: value, state: "" })}
          >
            <SelectTrigger data-testid="country-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {formData.country === "US" && (
          <div className="space-y-2">
            <Label>State</Label>
            <Select
              value={formData.state}
              onValueChange={(value) => setFormData({ ...formData, state: value })}
            >
              <SelectTrigger data-testid="state-select">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Address</Label>
        <Input
          placeholder="Street address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          data-testid="address-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>City</Label>
          <Input
            placeholder="City"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input
            placeholder="Phone number"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tax ID (SSN/EIN/VAT)</Label>
        <Input
          placeholder="Tax identification number"
          value={formData.tax_id}
          onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
          data-testid="tax-id-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Input
          placeholder="Additional notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => {
          setShowAddDialog(false);
          setShowEditDialog(false);
        }}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          className="bg-[#2563eb] hover:bg-[#1d4ed8]"
          disabled={submitting}
          data-testid="save-recipient-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            isEdit ? "Update" : "Add"
          )}
        </Button>
      </DialogFooter>
    </form>
  );

  const RecipientTable = ({ data }) => (
    <Card className="border border-gray-200">
      <CardContent className="p-0">
        {data.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No {activeTab === "customers" ? "customers" : "vendors"} yet
            </h3>
            <p className="text-gray-500 mb-4">
              Add your first {activeTab === "customers" ? "customer" : "vendor"}
            </p>
            <Button 
              className="bg-[#2563eb] hover:bg-[#1d4ed8] gap-2"
              onClick={openAddDialog}
            >
              <Plus className="w-4 h-4" />
              Add {activeTab === "customers" ? "Customer" : "Vendor"}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Name</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Email</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Entity</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Location</th>
                  {activeTab === "vendors" && (
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Tax Form</th>
                  )}
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.map((recipient) => (
                  <tr key={recipient.recipient_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {recipient.entity_type === "company" ? (
                          <Building2 className="w-4 h-4 text-gray-400" />
                        ) : (
                          <User className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="font-medium text-gray-900">{recipient.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{recipient.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary">
                        {recipient.entity_type === "company" ? "Company" : "Individual"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-500">
                        <Globe className="w-4 h-4" />
                        {COUNTRIES.find(c => c.code === recipient.country)?.name || recipient.country}
                        {recipient.state && `, ${recipient.state}`}
                      </div>
                    </td>
                    {activeTab === "vendors" && (
                      <td className="px-4 py-3">
                        {getTaxFormStatusBadge(recipient)}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`actions-${recipient.recipient_id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(recipient)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {activeTab === "vendors" && recipient.tax_form_status !== "received" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedRecipient(recipient);
                                  setShowTaxFormDialog(true);
                                }}
                              >
                                <FileText className="w-4 h-4 mr-2" />
                                Request Tax Form
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => {
                              setSelectedRecipient(recipient);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Recipients</h1>
            <p className="text-gray-500 mt-1">Manage your customers and vendors</p>
          </div>
          <Button 
            className="bg-[#2563eb] hover:bg-[#1d4ed8] gap-2" 
            onClick={openAddDialog}
            data-testid="add-recipient-btn"
          >
            <Plus className="w-4 h-4" />
            Add {activeTab === "customers" ? "Customer" : "Vendor"}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{vendors.length}</p>
                  <p className="text-sm text-gray-500">Vendors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{customers.length}</p>
                  <p className="text-sm text-gray-500">Customers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">
                    {vendors.filter(v => v.tax_form_status === "received").length}
                  </p>
                  <p className="text-sm text-gray-500">Forms Received</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">
                    {vendors.filter(v => v.tax_form_status === "requested").length}
                  </p>
                  <p className="text-sm text-gray-500">Pending Forms</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="vendors" className="gap-2" data-testid="vendors-tab">
                <Building2 className="w-4 h-4" />
                Vendors ({vendors.length})
              </TabsTrigger>
              <TabsTrigger value="customers" className="gap-2" data-testid="customers-tab">
                <User className="w-4 h-4" />
                Customers ({customers.length})
              </TabsTrigger>
            </TabsList>

            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-recipients"
              />
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <Card className="border border-gray-200 mt-4">
              <CardContent className="py-16">
                <div className="flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <TabsContent value="vendors" className="mt-4">
                <RecipientTable data={filteredRecipients} />
              </TabsContent>
              <TabsContent value="customers" className="mt-4">
                <RecipientTable data={filteredRecipients} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {/* Add Recipient Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Add {formData.recipient_type === "customer" ? "Customer" : "Vendor"}
            </DialogTitle>
            <DialogDescription>
              Enter the details for your new {formData.recipient_type === "customer" ? "customer" : "vendor"}
            </DialogDescription>
          </DialogHeader>
          <RecipientForm onSubmit={handleAdd} />
        </DialogContent>
      </Dialog>

      {/* Edit Recipient Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Recipient</DialogTitle>
            <DialogDescription>
              Update the details for {selectedRecipient?.name}
            </DialogDescription>
          </DialogHeader>
          <RecipientForm onSubmit={handleEdit} isEdit />
        </DialogContent>
      </Dialog>

      {/* Tax Form Request Dialog */}
      <Dialog open={showTaxFormDialog} onOpenChange={setShowTaxFormDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Tax Form</DialogTitle>
            <DialogDescription>
              Send a tax form request to {selectedRecipient?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedRecipient && (
              <>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Recommended:</strong> Based on {selectedRecipient.name}'s location 
                    ({COUNTRIES.find(c => c.code === selectedRecipient.country)?.name || selectedRecipient.country}) 
                    and entity type ({selectedRecipient.entity_type}), we recommend requesting:
                  </p>
                  <div className="mt-2 bg-white rounded p-3 border border-blue-200">
                    <p className="font-semibold text-gray-900">
                      {getRecommendedTaxForm(selectedRecipient)?.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {getRecommendedTaxForm(selectedRecipient)?.description}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Select Tax Form</Label>
                  <div className="space-y-2">
                    <Button
                      variant={selectedRecipient.country === "US" ? "default" : "outline"}
                      className="w-full justify-start gap-2"
                      onClick={() => handleRequestTaxForm("w9")}
                      disabled={submitting}
                      data-testid="request-w9-btn"
                    >
                      <FileText className="w-4 h-4" />
                      W-9 (US Taxpayers)
                    </Button>
                    <Button
                      variant={selectedRecipient.country !== "US" && selectedRecipient.entity_type === "individual" ? "default" : "outline"}
                      className="w-full justify-start gap-2"
                      onClick={() => handleRequestTaxForm("w8ben")}
                      disabled={submitting}
                      data-testid="request-w8ben-btn"
                    >
                      <FileText className="w-4 h-4" />
                      W-8BEN (Foreign Individuals)
                    </Button>
                    <Button
                      variant={selectedRecipient.country !== "US" && selectedRecipient.entity_type === "company" ? "default" : "outline"}
                      className="w-full justify-start gap-2"
                      onClick={() => handleRequestTaxForm("w8bene")}
                      disabled={submitting}
                      data-testid="request-w8bene-btn"
                    >
                      <FileText className="w-4 h-4" />
                      W-8BEN-E (Foreign Entities)
                    </Button>
                  </div>
                </div>

                {submitting && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">Sending request...</span>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaxFormDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recipient</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedRecipient?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
            >
              {submitting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
